import * as Tone from "tone";
import {pitchLatency, pitchRates} from "./defaults";
import {createMonoVoice} from "./mono";

type PlayerType = 'normal' | 'hi' | 'lo' | 'reverse';

interface ExportOptions {
    normalBuffers: Tone.ToneAudioBuffer[];
    reverseBuffers: Tone.ToneAudioBuffer[];
    slices: number[];
    samples: number[];
    retrigs: number[];
    playerTypes: PlayerType[];
    bpm: number;
    speed: number;
}

const getDateString = () => {
    return new Date((new Date()).getTime() - ((new Date()).getTimezoneOffset() * 60000)).toJSON().slice(0, 16).replace('T', '_').replace(':', '-')
}

// 32-bit float WAV so hot samples and pitch-shift tails can't clip on export
const encodeWavFloat32 = (buffer: AudioBuffer, frameOffset: number, frameCount: number): Blob => {
    const numChannels = buffer.numberOfChannels
    const blockAlign = numChannels * 4
    const dataSize = frameCount * blockAlign
    const headerSize = 56 // RIFF(12) + fmt(24) + fact(12) + data header(8)
    const arrayBuffer = new ArrayBuffer(headerSize + dataSize)
    const view = new DataView(arrayBuffer)

    const writeString = (offset: number, value: string) => {
        for (let i = 0; i < value.length; i++) {
            view.setUint8(offset + i, value.charCodeAt(i))
        }
    }

    writeString(0, 'RIFF')
    view.setUint32(4, headerSize + dataSize - 8, true)
    writeString(8, 'WAVE')
    writeString(12, 'fmt ')
    view.setUint32(16, 16, true)
    view.setUint16(20, 3, true) // IEEE float
    view.setUint16(22, numChannels, true)
    view.setUint32(24, buffer.sampleRate, true)
    view.setUint32(28, buffer.sampleRate * blockAlign, true)
    view.setUint16(32, blockAlign, true)
    view.setUint16(34, 32, true)
    writeString(36, 'fact')
    view.setUint32(40, 4, true)
    view.setUint32(44, frameCount, true)
    writeString(48, 'data')
    view.setUint32(52, dataSize, true)

    const channels: Float32Array[] = []
    for (let ch = 0; ch < numChannels; ch++) {
        channels.push(buffer.getChannelData(ch))
    }

    let offset = headerSize
    for (let i = 0; i < frameCount; i++) {
        for (let ch = 0; ch < numChannels; ch++) {
            view.setFloat32(offset, channels[ch][frameOffset + i], true)
            offset += 4
        }
    }

    return new Blob([arrayBuffer], {type: 'audio/wav'})
}

const exportLoop = async ({normalBuffers, reverseBuffers, slices, samples, retrigs, playerTypes, bpm, speed}: ExportOptions) => {
    const sampleRate = Tone.getContext().sampleRate
    // bpm already carries the speed offset, the players need it as playback rate
    const rate = 1 + (speed / 100)
    const loopSeconds = (60 / bpm) * 8 // 16 steps of 8th notes = 2 bars
    const loopFrames = Math.round(loopSeconds * sampleRate)
    const stepSeconds = loopSeconds / 16

    // same fit as live playback: the first sample owns the grid, the others
    // are rate-fitted so their 16 slices land on the same 16 steps
    const sampleDurations = normalBuffers.map(buffer => buffer.duration)
    const sliceDurations = sampleDurations.map(duration => duration / 16)
    const fitRates = sampleDurations.map(duration => duration / sampleDurations[0])

    // Render the pattern twice and keep the second pass: retrig overlaps and
    // pitch-shift tails from the loop end fold into the downbeat, so the file
    // loops as seamlessly as live playback.
    const renderSeconds = (loopFrames * 2 + 1000) / sampleRate

    const rendered = await Tone.Offline(() => {
        const main = new Tone.Volume(-0.5).toDestination()
        const filter = new Tone.Filter(200, "highpass").connect(main)

        const pitchUp = new Tone.PitchShift({
            pitch: 6,
            windowSize: 0.03
        }).connect(filter)

        const outputs: Record<PlayerType, Tone.ToneAudioNode> = {
            'normal': filter,
            'hi': pitchUp,
            'lo': filter,
            'reverse': filter,
        }

        // same monophonic voice as live playback, fresh for this render
        const playMono = createMonoVoice()

        for (let pass = 0; pass < 2; pass++) {
            for (let step = 0; step < 16; step++) {
                const sliceIndex = slices[step]
                if (sliceIndex === -1) {
                    continue
                }

                const playerType = playerTypes[step]
                const sampleIndex = Math.min(samples[step] || 0, normalBuffers.length - 1)
                const fit = fitRates[sampleIndex]
                const time = Math.max(0, pass * loopSeconds + step * stepSeconds - (pitchLatency[playerType] || 0))
                const offset = sliceIndex * sliceDurations[sampleIndex]
                const nextStepEmpty = slices[step + 1] === -1

                // stepSeconds is real time, sliceDuration is buffer time —
                // a step holds `stepSeconds * rate * fit` seconds of this sample's material
                let playDuration = Math.min(sliceDurations[sampleIndex], stepSeconds * rate * fit)
                if (nextStepEmpty) {
                    playDuration *= 2
                }

                playMono({
                    buffer: playerType === 'reverse' ? reverseBuffers[sampleIndex] : normalBuffers[sampleIndex],
                    output: outputs[playerType],
                    time,
                    offset,
                    duration: playDuration,
                    rate: rate * fit,
                    pitchRate: pitchRates[playerType] || 1,
                    retrig: retrigs[step],
                })
            }
        }
    }, renderSeconds, 2, sampleRate)

    const audioBuffer = rendered.get()
    if (!audioBuffer || audioBuffer.length < loopFrames * 2) {
        throw new Error('Offline render came back shorter than the loop')
    }

    const blob = encodeWavFloat32(audioBuffer, loopFrames, loopFrames)

    const bpmLabel = Math.round(bpm * 10) / 10
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${getDateString()}_breakcore_mangler_${bpmLabel}_bpm.wav`
    a.click()
    URL.revokeObjectURL(url)
}

export {exportLoop}
